import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import { addPreLeadNote, getPreLeadNotes } from '../../services/api/implementation/impl';
import {
  formatPreLeadNoteDate,
  getPreLeadLegacyLog,
  getPreLeadLogAuthor,
  getPreLeadLogDate,
  getPreLeadLogText,
  getPreLeadReason,
  hasPreleadNotes,
  isPreleadSettled,
  sortPreLeadLogsNewestFirst
} from '../../utils/preleadLogUtils';

const NOTE_BUTTON_CLASS =
  'inline-flex items-center rounded-lg border border-amber-600/40 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-100 transition-colors';

export function PreleadNoteButton({ row, onClick }) {
  const label = hasPreleadNotes(row) ? 'View notes' : 'Add note';

  return (
    <button type="button" onClick={onClick} className={NOTE_BUTTON_CLASS}>
      {label}
    </button>
  );
}

function NoteEntry({ entry }) {
  const text = getPreLeadLogText(entry);
  const author = getPreLeadLogAuthor(entry);
  const dateLabel = formatPreLeadNoteDate(getPreLeadLogDate(entry));

  if (!text) return null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <p className="whitespace-pre-wrap text-sm text-gray-900">{text}</p>
      {(author !== '—' || dateLabel) && (
        <p className="mt-2 text-xs text-gray-500">
          {[author !== '—' ? author : null, dateLabel].filter(Boolean).join(' · ')}
        </p>
      )}
    </div>
  );
}

const PreleadLogModal = ({ prelead, onClose, onLogAdded }) => {
  const queryClient = useQueryClient();
  const preleadId = prelead?.id ?? prelead?.pre_lead_id;
  const displayName =
    prelead?.name && prelead.name !== '—' ? prelead.name : `Prelead #${preleadId ?? ''}`;
  const settled = isPreleadSettled(prelead);
  const isQualified = prelead?.lead_id != null;
  const reason = getPreLeadReason(prelead);

  const [noteText, setNoteText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: notesData,
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ['preLeadNotes', preleadId],
    queryFn: () => getPreLeadNotes(preleadId),
    enabled: Boolean(preleadId)
  });

  const legacyLog = getPreLeadLegacyLog(notesData) || getPreLeadLegacyLog(prelead);
  const notes = sortPreLeadLogsNewestFirst(notesData?.notes ?? []);
  const hasAnyNotes = Boolean(legacyLog) || notes.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = noteText.trim();
    if (!trimmed) {
      toast.error('Please enter a note.');
      return;
    }
    if (!preleadId) {
      toast.error('Invalid prelead.');
      return;
    }

    setIsSubmitting(true);
    try {
      await addPreLeadNote(preleadId, trimmed);
      setNoteText('');
      await refetch();
      queryClient.invalidateQueries({ queryKey: ['preLeadNotes', preleadId] });
      toast.success('Note added.');
      onLogAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Could not save note.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal width={42} name={`Notes — ${displayName}`} onClose={onClose}>
      <div className="space-y-4">
        {settled && (
          <div className="flex flex-wrap gap-2">
            {isQualified && (
              <span className="inline-flex rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-semibold text-green-800 ring-1 ring-green-600/30">
                Qualified
              </span>
            )}
            {prelead?.is_disqualified && (
              <span className="inline-flex rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-800 ring-1 ring-red-600/30">
                Unqualified
              </span>
            )}
          </div>
        )}

        {reason ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Reason</p>
            <p className="mt-2 whitespace-pre-wrap text-sm text-gray-900">{reason}</p>
          </div>
        ) : null}

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Notes</p>

          {isLoading ? (
            <p className="text-sm text-gray-500">Loading notes…</p>
          ) : isError ? (
            <p className="text-sm text-red-600">Could not load notes.</p>
          ) : (
            <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
              {legacyLog ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                    Previous note
                  </p>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-gray-900">{legacyLog}</p>
                </div>
              ) : null}

              {notes.map((entry) => (
                <NoteEntry
                  key={entry.id ?? `${getPreLeadLogDate(entry)}-${getPreLeadLogText(entry)}`}
                  entry={entry}
                />
              ))}

              {!hasAnyNotes ? (
                <p className="text-sm text-gray-500">No notes yet. Add one below.</p>
              ) : null}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 border-t border-gray-200 pt-4">
          <label
            htmlFor="prelead-note-input"
            className="text-xs font-semibold uppercase tracking-wide text-gray-600">
            Add note
          </label>
          <textarea
            id="prelead-note-input"
            rows={3}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="e.g. Called today, follow up Monday"
            className="w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-customBlue focus:outline-none focus:ring-1 focus:ring-customBlue"
            disabled={isSubmitting}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Close
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !noteText.trim()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-customBlue px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
              {isSubmitting ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default PreleadLogModal;
